// @ts-nocheck — aspirational data shapes / API mismatch; tracked under Task #75 (type-safety debt)
import { Link } from "wouter";
import PLATFORM_URLS from "@/config/platform-urls";
import { 
  Mail, 
  Facebook, 
  Twitter, 
  Linkedin, 
  Instagram,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useCMSSettings } from "@/hooks/use-cms";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: settings } = useCMSSettings();
  const { t } = useTranslation();
  const { toast } = useToast();

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setEmail("");
        toast({
          title: t("footer.newsletter.success", "Subscribed!"),
          description: t("footer.newsletter.successDescription", "Thank you for subscribing to our newsletter."),
        });
      } else {
        toast({
          title: t("footer.newsletter.error", "Subscription failed"),
          description: data.error || "Please try again later.",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: t("footer.newsletter.error", "Subscription failed"),
        description: "Network error. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentYear = new Date().getFullYear();

  const footerSections = {
    services: {
      title: t("footer.services.title", "Services"),
      links: [
        { href: "/services#freight", label: t("footer.services.freight", "Freight") },
        { href: "/services#warehouse", label: t("footer.services.warehouse", "Warehousing") },
        { href: "/services#distribution", label: t("footer.services.distribution", "Distribution") },
        { href: "/tracking", label: t("footer.services.tracking", "Tracking") }
      ]
    },
    solutions: {
      title: t("footer.solutions.title", "Solutions"),
      links: [
        { href: PLATFORM_URLS.mololink, label: t("footer.solutions.mololink", "MoloLink") },
        { href: "/departments", label: t("footer.solutions.departments", "Departments") },
        { href: "/partners", label: t("footer.solutions.partners", "Partners") },
        { href: "/services", label: t("footer.solutions.allServices", "All Services") }
      ]
    },
    company: {
      title: t("footer.company.title", "Company"),
      links: [
        { href: "/about", label: t("footer.company.aboutUs", "About Us") },
        { href: "/partners", label: t("footer.company.partners", "Partners") },
        { href: "/contact", label: t("footer.company.contact", "Contact") },
        { href: "/careers", label: t("footer.company.careers", "Careers") }
      ]
    },
    support: {
      title: t("footer.support.title", "Support"),
      links: [
        { href: "/contact", label: t("footer.support.helpSupport", "Help & Support") },
        { href: "/contact", label: t("footer.support.getQuote", "Get a Quote") },
        { href: "/privacy", label: t("footer.support.privacy", "Privacy Policy") },
        { href: "/terms", label: t("footer.support.terms", "Terms of Service") }
      ]
    }
  };

  const socialLinks = [
    { icon: Facebook, href: settings?.facebook_url || "https://facebook.com/MOLOCHAIN", label: "Facebook" },
    { icon: Twitter, href: settings?.twitter_url || "https://twitter.com/MOLOCHAIN", label: "Twitter" },
    { icon: Linkedin, href: settings?.linkedin_url || "https://linkedin.com/company/molochain", label: "LinkedIn" },
    { icon: Instagram, href: settings?.instagram_url || "https://instagram.com/MOLOCHAIN", label: "Instagram" }
  ];

  return (
    <footer className="relative bg-gradient-to-b from-background to-primary/5 border-t">
      <div className="border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold flex items-center gap-2 justify-center md:justify-start">
                <Sparkles className="h-6 w-6 text-primary" />
                {t("footer.newsletter.title", "Stay Updated")}
              </h3>
              <p className="mt-2 text-muted-foreground">
                {t("footer.newsletter.description", "Get the latest updates and insights delivered to your inbox.")}
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2 w-full md:w-auto">
              <input type="text" name="website" style={{ position: "absolute", left: "-9999px" }} tabIndex={-1} autoComplete="off" />
              <Input
                type="email"
                placeholder={t("footer.newsletter.placeholder", "Enter your email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full md:w-64"
                required
              />
              <Button type="submit" disabled={isSubmitting} className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary">
                {isSubmitting ? t("footer.newsletter.subscribing", "Sending...") : t("footer.newsletter.subscribe", "Subscribe")}
                {!isSubmitting && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <Link href="/">
                <div className="flex items-center gap-3 group cursor-pointer">
                  <img 
                    src="/molochain-logo.png" 
                    alt="MOLOCHAIN" 
                    className="h-10 w-auto transition-transform group-hover:scale-105"
                  />
                  <div>
                    <h3 className="text-xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                      MOLOCHAIN
                    </h3>
                    <p className="text-xs text-muted-foreground">{t("footer.brand.tagline", "Global Logistics Ecosystem")}</p>
                  </div>
                </div>
              </Link>
            </div>
            
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("footer.brand.description", "Global trade and logistics platform connecting businesses worldwide.")}
            </p>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Mail className="h-4 w-4 text-primary" />
                <span>{settings?.contact_email || "molochain@molochain.com"}</span>
              </div>
            </div>

            <div className="flex gap-2">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="p-2 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors duration-200"
                >
                  <social.icon className="h-4 w-4 text-primary" />
                </a>
              ))}
            </div>
          </div>

          {Object.entries(footerSections).map(([key, section]) => (
            <div key={key}>
              <h3 className="font-semibold text-foreground mb-4">{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link, index) => (
                  <li key={`${link.href}-${index}`}>
                    {link.href.startsWith("http://") || link.href.startsWith("https://") ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-200 cursor-pointer group"
                      >
                        <span className="group-hover:translate-x-0.5 transition-transform">
                          {link.label}
                        </span>
                      </a>
                    ) : (
                      <Link href={link.href}>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-200 cursor-pointer group">
                          <span className="group-hover:translate-x-0.5 transition-transform">
                            {link.label}
                          </span>
                        </div>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 pt-8 border-t">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              {t("footer.copyright", { year: currentYear })}
            </p>
            <div className="flex items-center gap-6 text-sm">
              <Link href="/privacy">
                <span className="text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                  {t("footer.links.privacy", "Privacy")}
                </span>
              </Link>
              <Link href="/terms">
                <span className="text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                  {t("footer.links.terms", "Terms")}
                </span>
              </Link>
              <Link href="/cookies">
                <span className="text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                  {t("footer.links.cookies", "Cookies")}
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
